import type { SearchSelectOption } from './place_option.js';

/** O que a opção lê de um aeroporto. Estrutural: o `Airport` do wire
 *  (`GET /flight/airports` e o histórico) cabe nele sem adaptador. */
export interface AirportSource {
  iata: string;
  name: string;
  countryName: string;
  countryCode: string;
}

/**
 * O que o kit não sabe sozinho para nomear um aeroporto: as tabelas do domain
 * (passadas com o MESMO nome das funções de `@voll-intelligence/domain`), o
 * nome do país e o sufixo dos códigos metro, já no idioma da sessão. O kit não
 * importa o domain (#4998); o genui-kit monta este objeto em
 * `airportNaming(locale)`, e o host o memoiza por locale.
 */
export interface AirportNaming {
  readonly cityByAirport: (iata: string) => string | null;
  readonly stateByAirport: (iata: string) => string | null;
  readonly distinguishingAirportName: (iata: string) => string | null;
  readonly isMetroCode: (iata: string) => boolean;
  /** O nome de exibição do país pelo alpha-2; `undefined` quando não se sabe. */
  readonly countryName: (code: string) => string | undefined;
  /** "todos os aeroportos", o sufixo do título de um código metro. */
  readonly allAirports: string;
}

/**
 * **Aeroporto → opção do combobox DE/PARA.** Uma implementação para a conversa e
 * as páginas.
 *
 * O rótulo é `IATA + CIDADE` (`SDU Rio de Janeiro`), e a cidade vem do domain
 * (`naming.cityByAirport`) — a MESMA autoridade que o filtro de ofertas usa pra agrupar por
 * cidade. Antes o rótulo era o `name` do fornecedor mais o IATA no fim
 * ("Aeroporto Internacional de Florianópolis · FLN", e às vezes o endereço inteiro):
 * estourava a pílula do campo, e o código — a única parte que o fio realmente usa —
 * ficava no canto que trunca primeiro.
 *
 * O `name` não morre, vira a LEGENDA da linha (`detail.caption`, a receita de duas
 * linhas do protótipo): é o que desempata dois aeroportos da mesma cidade
 * (GRU × CGH), fechada com `· país` quando o fio o traz. Quando o domain não
 * conhece o código (fora das ~7k entradas), o `name` sobe pro título — um IATA
 * sozinho não diz onde é.
 *
 * Código METRO ("todos os aeroportos": SAO, RIO…, #2429) tem título CONSTRUÍDO —
 * `cidade (todos os aeroportos)` — em vez do `name` do fornecedor: o upstream manda
 * lixo nesse campo (PAR chega como "Franca, Paris (…)"; RIO chega com o display do
 * GALEÃO, que rotularia a opção como se fosse GIG). A legenda fica só com o país:
 * não há um segundo fato confiável a dizer. Glifo `city` (pino) — é a linha de
 * "São Paulo inteira", não a de um terminal (receita da `LinhaDaPraca`).
 *
 * Aeroporto dos EUA leva a sigla do estado no título (`PWM Portland, ME`, #4135): sem
 * ela, PWM (Maine) e PDX (Oregon) eram duas linhas iguais. A sigla vem do domain
 * (`stateByAirport`) e só entra com a cidade do domain — colada no `name` do
 * fornecedor diria "Campo de Teste, ME". Metro não ganha sigla. `cityByAirport` segue a
 * cidade nua: é por ela que o filtro de ofertas agrupa.
 *
 * Cidade com mais de um aeroporto leva o NOME dele no título (`DAL Dallas, TX — Love
 * Field`, #4157): "Dallas, TX" valia para Love Field e para Dallas/Fort Worth, e o nome
 * na legenda (quando o fornecedor o mandava limpo) não bastava como desempate. O nome
 * vem do domain (`distinguishingAirportName`), que só o devolve quando a cidade tem
 * mais de um aeroporto nomeado — FLN segue `FLN Florianópolis`.
 *
 * O país da legenda sai do `countryCode` no idioma da sessão (`Estados Unidos`): o
 * `countryName` do fornecedor é o nome ISO cru ("United States of America (the)") e só
 * aparece quando o código não resolve.
 *
 * `value` segue o IATA cru do fornecedor: é o que o wire exige, e normalizá-lo aqui
 * mudaria o que o echo manda.
 */
export function airportOptionOf(a: AirportSource, naming: AirportNaming): SearchSelectOption {
  const code = a.iata.trim().toUpperCase();
  const name = a.name.trim();
  const country = naming.countryName(a.countryCode) ?? a.countryName.trim();
  if (naming.isMetroCode(code)) {
    const title = `${naming.cityByAirport(code) ?? code} (${naming.allAirports})`;
    return {
      value: a.iata,
      label: `${code} ${title}`,
      detail: { code, title, icon: 'city', ...(country ? { caption: country } : {}) },
    };
  }
  const city = naming.cityByAirport(code);
  const state = city ? naming.stateByAirport(code) : null;
  const place = city ?? name;
  const airport = city ? naming.distinguishingAirportName(code) : null;
  const title = [state ? `${place}, ${state}` : place, airport].filter(Boolean).join(' — ');
  // A legenda só carrega o que diz algo NOVO: com a cidade desconhecida o `name` já
  // subiu pro título, e repeti-lo seria a mesma palavra duas vezes na linha. Compara
  // com o lugar, não com o título: "Miami" não é novo ao lado de "Miami, FL" — nem
  // "Congonhas" ao lado de "São Paulo — Congonhas".
  const repeated = name === place || name === airport;
  const caption = [name && !repeated ? name : '', country].filter(Boolean).join(' · ');
  return {
    value: a.iata,
    // Título vazio (cidade desconhecida e `name: ''`, que o fornecedor manda): o código
    // sozinho, sem o espaço pendurado.
    label: title ? `${code} ${title}` : code,
    detail: { code, title, icon: 'airport', ...(caption ? { caption } : {}) },
  };
}

/** O rótulo da pílula DE/PARA para um IATA cru (`GRU São Paulo — Guarulhos`,
 *  `SAO São Paulo (todos os aeroportos)`): a mesma convenção de `airportOptionOf`,
 *  sem os dados do fornecedor. É o `airportLabel` que os hosts passam ao
 *  `PlannerForm`; código fora do cadastro volta como o próprio código. */
export function airportLabelOf(iata: string, naming: AirportNaming): string {
  return airportOptionOf({ iata, name: '', countryName: '', countryCode: '' }, naming).label;
}
